import {ApolloClient, ApolloQueryResult, gql} from '@apollo/client';
import {errorResponse, NetworkResult, successResponse} from './NetworkResult';
import {FullSnippet, Snippet} from '../Models/Snippet';
import {Duration} from '../Domain/Duration';
import {Distance} from '../Domain/Distance';
import {Power} from '../Domain/Power';
import {PUBLIC_WORKOUT_FRAGMENT} from './Fragments/Workout';
import {WorkoutsService} from './WorkoutsService';
import {DateRange} from '../Domain/DateRange';
import {formatISO} from 'date-fns';
import {Force} from '../Domain/Force';
import {Energy} from '../Domain/Energy';
import {SnippetCollection} from '../Models/SnippetCollection';
import {FullWorkout} from '../Models/FullWorkout';
import {PublicUser} from '../Models/PublicUser';
import {Fraction} from '../Domain/Fraction';

export interface SnippetInput {
    workoutId: number;
    reportId?: number | null;
    startTimeMillis: number;
    endTimeMillis: number;
    elapsedTimeMillis: number;
    traveledDistanceMillimeters: number;
    averagePowerWatts: number | null;
    averageRelativePeakForcePosition: number | null;
    averageSplitMillis: number | null;
    averagePeakForce: number | null;
    averageStrokeLengthMillimeters: number | null;
    averageDriveTimeMillis: number | null;
    averageRecoverTimeMillis: number | null;
    averageEnergyPerStroke: number | null;
}

const CREATE_SNIPPET_MUTATION = gql(`
    mutation(
        $workoutId: Int!
        $startTimeMillis: Int
        $endTimeMillis: Int
        $elapsedTimeMillis: Float!
        $traveledDistanceMillimeters: Float!
        $averagePowerWatts: Float
        $averageRelativePeakForcePosition: Float
        $averageSplitMillis: Float
        $averagePeakForce: Float
        $averageStrokeLengthMillimeters: Float
        $averageDriveTimeMillis: Float
        $averageRecoverTimeMillis: Float
        $averageEnergyPerStroke: Float
    ) {
        createSnippet(
            workout_id: $workoutId
            start_time_millis: $startTimeMillis
            end_time_millis: $endTimeMillis
            elapsed_time_millis: $elapsedTimeMillis
            traveled_distance_millimeters: $traveledDistanceMillimeters
            average_power_watts: $averagePowerWatts
            average_relative_peak_force_position: $averageRelativePeakForcePosition
            average_split_millis: $averageSplitMillis
            average_peak_force: $averagePeakForce
            average_stroke_length_millimeters: $averageStrokeLengthMillimeters
            average_drive_time_millis: $averageDriveTimeMillis
            average_recover_time_millis: $averageRecoverTimeMillis
            average_energy_per_stroke: $averageEnergyPerStroke
        ) {
            id
        }
    }
`);

const CREATE_SNIPPET_FOR_REPORT_MUTATION = gql(`
    mutation(
        $workoutId: Int!
        $reportId: Int
        $startTimeMillis: Int
        $endTimeMillis: Int
        $elapsedTimeMillis: Float!
        $traveledDistanceMillimeters: Float!
        $averagePowerWatts: Float
        $averageRelativePeakForcePosition: Float
        $averageSplitMillis: Float
        $averagePeakForce: Float
        $averageStrokeLengthMillimeters: Float
        $averageDriveTimeMillis: Float
        $averageRecoverTimeMillis: Float
        $averageEnergyPerStroke: Float
    ) {
        createSnippet(
            workout_id: $workoutId
            report_id: $reportId
            start_time_millis: $startTimeMillis
            end_time_millis: $endTimeMillis
            elapsed_time_millis: $elapsedTimeMillis
            traveled_distance_millimeters: $traveledDistanceMillimeters
            average_power_watts: $averagePowerWatts
            average_relative_peak_force_position: $averageRelativePeakForcePosition
            average_split_millis: $averageSplitMillis
            average_peak_force: $averagePeakForce
            average_stroke_length_millimeters: $averageStrokeLengthMillimeters
            average_drive_time_millis: $averageDriveTimeMillis
            average_recover_time_millis: $averageRecoverTimeMillis
            average_energy_per_stroke: $averageEnergyPerStroke
        ) {
            id
        }
    }
`);

const UPDATE_SNIPPET_MUTATION = gql`
    mutation ($id: Int!, $description: String) {
        updateSnippet(id: $id, description: $description) {
            id
        }
    }
`;

const START_SNIPPET_POST_PROCESSING_MUTATION = gql`
    mutation ($snippetIds: [Int!]!) {
        startSnippetPostProcessing(snippet_ids: $snippetIds) {
            id
        }
    }
`;

const GET_SNIPPETS_QUERY = gql`
    query {
        snippets {
            id
            description
            start_time_millis
            end_time_millis
            elapsed_time_millis
            traveled_distance_millimeters
            average_power_watts
            average_split_millis
            average_peak_force
            average_relative_peak_force_position
            average_drive_time_millis
            average_recover_time_millis
            average_energy_per_stroke
            average_stroke_length_millimeters
            rsquared_total_stroke
            rsquared_pre_peak
            rsquared_post_peak
            started_processing_at_iso8601
            snippet_collections {
                id
                name
            }
            public_workout {
                id
                executed_at_iso8601
                public_user {
                    id
                    display_name
                }
            }
        }
    }
`;

const GET_SNIPPET_QUERY = gql`
    ${PUBLIC_WORKOUT_FRAGMENT}
    query ($id: Int!) {
        snippet(id: $id) {
            id
            description
            start_time_millis
            end_time_millis
            elapsed_time_millis
            traveled_distance_millimeters
            average_power_watts
            average_split_millis
            average_peak_force
            average_relative_peak_force_position
            average_drive_time_millis
            average_recover_time_millis
            average_energy_per_stroke
            average_stroke_length_millimeters
            rsquared_total_stroke
            rsquared_pre_peak
            rsquared_post_peak
            started_processing_at_iso8601
            snippet_collections {
                id
                name
            }
            public_workout {
                ...FullWorkout
                public_user {
                    id
                    display_name
                }
            }
        }
    }
`;
const GET_FILTERED_SNIPPETS_QUERY = gql`
    query (
        $snippetCollectionIds: [Int!]
        $userIds: [Int!]
        $crewIds: [Int!]
        $fromIso8601: String
        $tillIso8601: String
    ) {
        snippets(
            snippet_collection_ids: $snippetCollectionIds
            user_ids: $userIds
            crew_ids: $crewIds
            from_iso8601: $fromIso8601
            till_iso8601: $tillIso8601
        ) {
            id
            description
            start_time_millis
            end_time_millis
            elapsed_time_millis
            traveled_distance_millimeters
            average_power_watts
            average_split_millis
            average_peak_force
            average_relative_peak_force_position
            average_drive_time_millis
            average_recover_time_millis
            average_energy_per_stroke
            average_stroke_length_millimeters
            rsquared_total_stroke
            rsquared_pre_peak
            rsquared_post_peak
            started_processing_at_iso8601
            snippet_collections {
                id
                name
            }
            public_workout {
                id
                title
                training_plan {
                    title
                }
                executed_at_iso8601
                public_user {
                    id
                    display_name
                }
            }
        }
    }
`;

export class SnippetService {
    constructor(private client: ApolloClient<any>) {}

    public async find(id: number): Promise<NetworkResult<FullSnippet>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: GET_SNIPPET_QUERY,
                variables: {
                    id,
                },
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(this.toFullSnippet(apolloQueryResult.data.snippet));
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async create(input: SnippetInput): Promise<NetworkResult<{id: number}>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: CREATE_SNIPPET_MUTATION,
                variables: {
                    workoutId: input.workoutId,
                    startTimeMillis: Math.round(input.startTimeMillis),
                    endTimeMillis: Math.round(input.endTimeMillis),
                    elapsedTimeMillis: input.elapsedTimeMillis !== null ? Math.round(input.elapsedTimeMillis) : null,
                    traveledDistanceMillimeters: input.traveledDistanceMillimeters,
                    averagePowerWatts: input.averagePowerWatts,
                    averageRelativePeakForcePosition: input.averageRelativePeakForcePosition,
                    averageSplitMillis: input.averageSplitMillis !== null ? Math.round(input.averageSplitMillis) : null,
                    averagePeakForce: input.averagePeakForce,
                    averageStrokeLengthMillimeters: input.averageStrokeLengthMillimeters,
                    averageDriveTimeMillis:
                        input.averageDriveTimeMillis !== null ? Math.round(input.averageDriveTimeMillis) : null,
                    averageRecoverTimeMillis:
                        input.averageRecoverTimeMillis !== null ? Math.round(input.averageRecoverTimeMillis) : null,
                    averageEnergyPerStroke: input.averageEnergyPerStroke,
                },
                refetchQueries: [GET_FILTERED_SNIPPETS_QUERY, GET_SNIPPETS_QUERY],
            });

            // Make sure to reset the entire cache after a mutation, it has too many unexpected side effects otherwise
            await this.client.resetStore();

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(apolloQueryResult.data.createSnippet);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async createForReport(input: SnippetInput): Promise<NetworkResult<{id: number}>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: CREATE_SNIPPET_FOR_REPORT_MUTATION,
                variables: input,
                refetchQueries: [GET_FILTERED_SNIPPETS_QUERY, GET_SNIPPETS_QUERY],
            });

            // Make sure to reset the entire cache after a mutation, it has too many unexpected side effects otherwise
            await this.client.resetStore();

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(apolloQueryResult.data.createSnippet);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async get(): Promise<NetworkResult<Snippet[]>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: GET_SNIPPETS_QUERY,
            });
            return this.toResponse(apolloQueryResult);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async getFiltered(
        snippetCollectionIds: number[],
        userIds: number[],
        crewIds: number[],
        dateRange: DateRange | null,
    ): Promise<NetworkResult<Snippet[]>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: GET_FILTERED_SNIPPETS_QUERY,
                variables: {
                    snippetCollectionIds: snippetCollectionIds.length === 0 ? null : snippetCollectionIds,
                    userIds: userIds.length === 0 ? null : userIds,
                    crewIds: crewIds.length === 0 ? null : crewIds,
                    fromIso8601: dateRange?.from ? formatISO(dateRange.from) : null,
                    tillIso8601: dateRange?.till ? formatISO(dateRange.till) : null,
                },
            });
            return this.toResponse(apolloQueryResult);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async delete(id: string): Promise<NetworkResult<any>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    mutation DeleteSnippet($id: ID!) {
                        deleteSnippet(id: $id)
                    }
                `,
                variables: {
                    id,
                },
                refetchQueries: [GET_SNIPPETS_QUERY],
            });

            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(null);
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async updateSnippet(id: number, description: string | null): Promise<void> {
        try {
            await this.client.mutate({
                mutation: UPDATE_SNIPPET_MUTATION,
                variables: {
                    id,
                    description,
                },
                refetchQueries: [GET_FILTERED_SNIPPETS_QUERY, GET_SNIPPETS_QUERY],
            });
        } catch (e) {
            console.error(e);
        }
    }

    public async startPostProcessing(snippetIds: number[]): Promise<void> {
        try {
            await this.client.mutate({
                mutation: START_SNIPPET_POST_PROCESSING_MUTATION,
                variables: {
                    snippetIds,
                },
                refetchQueries: [GET_FILTERED_SNIPPETS_QUERY, GET_SNIPPETS_QUERY],
            });
        } catch (e) {
            console.error(e);
        }
    }

    private toResponse(apolloQueryResult: ApolloQueryResult<any>): NetworkResult<Snippet[]> {
        if (apolloQueryResult.errors) {
            return errorResponse();
        }

        return successResponse(
            apolloQueryResult.data.snippets.map((snippet: any): Snippet => {
                return this.toSnippet(snippet);
            }),
        );
    }

    private toSnippet(snippet: any): Snippet {
        return {
            id: snippet.id,
            description: snippet.description,
            startTime: Duration.fromMilliseconds(snippet.start_time_millis),
            endTime: Duration.fromMilliseconds(snippet.end_time_millis),
            elapsedTime: Duration.fromMilliseconds(snippet.elapsed_time_millis),
            traveledDistance: Distance.fromMillimeters(snippet.traveled_distance_millimeters),
            averagePower: snippet.average_power_watts !== null ? Power.fromWatts(snippet.average_power_watts) : null,
            averageSplit:
                snippet.average_split_millis !== null ? Duration.fromMilliseconds(snippet.average_split_millis) : null,
            averagePeakForce: snippet.average_peak_force !== null ? Force.fromNewton(snippet.average_peak_force) : null,
            averageRelativePeakForcePosition:
                snippet.average_relative_peak_force_position !== null
                    ? Fraction.fromValue(snippet.average_relative_peak_force_position)
                    : null,
            averageDriveTime:
                snippet.average_drive_time_millis !== null
                    ? Duration.fromMilliseconds(snippet.average_drive_time_millis)
                    : null,
            averageRecoverTime:
                snippet.average_recover_time_millis !== null
                    ? Duration.fromMilliseconds(snippet.average_recover_time_millis)
                    : null,
            averageEnergyPerStroke:
                snippet.average_energy_per_stroke !== null
                    ? Energy.fromJoules(snippet.average_energy_per_stroke)
                    : null,
            averageStrokeLength: snippet.average_stroke_length_millimeters
                ? Distance.fromMillimeters(snippet.average_stroke_length_millimeters)
                : null,
            rSquaredTotalStroke:
                snippet.rsquared_total_stroke !== null ? Fraction.fromValue(snippet.rsquared_total_stroke) : null,
            rSquaredPrePeak: snippet.rsquared_pre_peak !== null ? Fraction.fromValue(snippet.rsquared_pre_peak) : null,
            rSquaredPostPeak:
                snippet.rsquared_post_peak !== null ? Fraction.fromValue(snippet.rsquared_post_peak) : null,
            startedProcessingAt: snippet.started_processing_at_iso8601
                ? new Date(snippet.started_processing_at_iso8601)
                : null,
            snippetCollections: snippet.snippet_collections.map((snippetCollection: any) => {
                return {
                    id: snippetCollection.id,
                    name: snippetCollection.name,
                };
            }),
            publicWorkout: {
                id: snippet.public_workout.id,
                title: snippet.public_workout.title,
                trainingPlan: 
                    snippet.public_workout.training_plan === null ? null : {
                        title: snippet.public_workout.training_plan.title !== null ? snippet.public_workout.training_plan.title : null,
                    },
                executedAt: new Date(snippet.public_workout.executed_at_iso8601),
                publicUser:
                    (snippet.public_workout?.public_user && {
                        id: snippet.public_workout.public_user.id,
                        displayName: snippet.public_workout.public_user.display_name,
                    }) ||
                    null,
            },
        };
    }

    private toFullSnippet(rawSnippet: any): FullSnippet {
        const snippet = this.toSnippet(rawSnippet);

        return {
            ...snippet,
            snippetCollections: rawSnippet.snippet_collections.map((snippetCollection: any) => {
                return {
                    id: snippetCollection.id,
                    name: snippetCollection.name,
                };
            }),
            publicWorkout: {
                ...snippet.publicWorkout,
                ...WorkoutsService.getFullWorkout(rawSnippet.public_workout),
            },
        };
    }
}
